import { FC, useCallback, useMemo, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  FiltersActionsApply,
  LoaderMui,
  TopHeaderCalendar,
} from "@ryde/controls";
import {
  FinalSchemaService,
  PageHeader,
  PageHeaderFilters,
} from "@ryde/layout";
import { PageHeaderContext, useInitViewsAndFilters } from "@ryde/redux";
import {
  bulkOperationsDrawerAF,
  isGlobalLoaderActiveSelector,
  RydeUiUnit,
  selectedCoursesCalendarSlotIdsAtom,
} from "@ryde/shared";

import { CourseCrudRoutes } from "../crud";
import { useCoursesCalendarSlots } from "../hooks/use-courses-calendar-slots";
import {
  CoursesBulkOperationsCalendarDrawer,
  CoursesFloatingMenuCalendar,
  CoursesPanelCalendar,
  CoursesPanelTop,
} from "../panels";

const entity: RydeUiUnit = "course";

export const CoursesCalendarPageMain: FC = () => {
  const { t } = useTranslation();
  const [, startTransition] = useTransition();
  const [isPageReady, setIsPageReady] = useState(false);
  // `isPageReady` gates the full-page loader below, so the init must neither open it before the view request
  // IDs resolve nor leave it up when a views request fails.
  useInitViewsAndFilters({
    entity,
    onInitialized: () => setIsPageReady(true),
    onInitSkipped: () => setIsPageReady(false),
  });
  const { refetch, slots, createSlotRide } = useCoursesCalendarSlots();
  const setSelectedCourseSlots = useSetRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const isGlobalLoaderActive = useRecoilValue(isGlobalLoaderActiveSelector);
  const shouldShowPageLoader = !isPageReady && !isGlobalLoaderActive;

  const onAfterCalendarBulk = useCallback((): void => {
    void refetch();
    setSelectedCourseSlots(new Set());
  }, [refetch, setSelectedCourseSlots]);

  const onAfterSlotProduce = useCallback((): void => {
    void refetch();
  }, [refetch]);

  const { scheduledCoursesCount, courseTemplatesCount, ridesCreatedCount } =
    useMemo(() => {
      const courseIds = new Set<number>();
      let rides = 0;
      for (const s of slots) {
        courseIds.add(s.courseId);
        if (s.hasRide) {
          rides += 1;
        }
      }
      return {
        scheduledCoursesCount: slots.length,
        courseTemplatesCount: courseIds.size,
        ridesCreatedCount: rides,
      };
    }, [slots]);

  const onFiltersApply = useEvent((): void => {
    startTransition(() => {
      void refetch();
    });
  });

  const headerContextValue = useMemo(
    () => ({
      onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    }),
    [onFiltersApply],
  );

  return (
    <>
      <FinalSchemaService />
      <PageHeader
        mainTitle={t("course.coursesCalendarTitle")}
        topWidgetComponent={<TopHeaderCalendar entity={entity} />}
        bottomWidgetComponent={
          <CoursesPanelTop
            scheduledCoursesCount={scheduledCoursesCount}
            courseTemplatesCount={courseTemplatesCount}
            ridesCreatedCount={ridesCreatedCount}
          />
        }
      >
        <PageHeaderContext.Provider value={headerContextValue}>
          <PageHeaderFilters Actions={<FiltersActionsApply />} />
        </PageHeaderContext.Provider>
      </PageHeader>

      <CoursesPanelCalendar
        slots={slots}
        onCreateSlotRide={createSlotRide}
        onAfterSlotProduce={onAfterSlotProduce}
      />

      <CoursesFloatingMenuCalendar
        createSlotRide={createSlotRide}
        onAfterProduce={onAfterCalendarBulk}
      />

      {bulkOperationsDrawerOpened && <CoursesBulkOperationsCalendarDrawer />}

      <CourseCrudRoutes />
      {shouldShowPageLoader && <LoaderMui loading={true} />}
    </>
  );
};
